import { EntityStoreService } from './../../services/entity-store.service';
import { ModalRouteService } from './../../services/modal-route.service';

import { catchError, debounceTime, distinctUntilChanged, pairwise, map, filter, share } from 'rxjs/operators';
import { Location } from '@angular/common';
import { throwError, Subscription, Observable, Subject, defer } from 'rxjs';
import { SapiEntityVersion } from './../../services/sapi.service';
import { Injectable, Directive, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { Router, CanDeactivate, RouterStateSnapshot, ActivatedRouteSnapshot, ActivatedRoute, Params } from '@angular/router';
import { NgForm, ValidationErrors, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { HotkeysService, Hotkey } from 'angular2-hotkeys';
import { TabItem } from './bb-page-header/bb-page-header.component';

import { AuthenticationService } from '../../services/authentication.service';
import { SapiService, SapiEntity, SapiError } from '../../services/sapi.service';
import { OverlayService, DialogDefinition, NoticeDefinition } from '../../services/overlay.service';
import { ServiceLocator } from '../../modules/abstract.module';
import { ReduxService } from '../../services/redux.service';
import { ChecklistItem } from '../../interfaces/checklistItem';
import { QuickeditService } from '../../services/quickedit.service';
import { bbDeepCopy } from '../../shared/utils/bbDeepCopy.util';


// this guard should be added to appropiate routes in the module's route configuration
@Injectable()
export class NavigateAwayGuard implements CanDeactivate<AbstractDetailsComponent> {

	canDeactivate(component: AbstractDetailsComponent, currentRoute: ActivatedRouteSnapshot,
		currentState: RouterStateSnapshot, nextState: RouterStateSnapshot) {

		return component?.allowNavigateAway ? component.allowNavigateAway(currentState, nextState) : true;
	}
}

export interface EntityConfiguration {
	name: string;
	basePath: string;
	parentPath: string;
	backOnNewCancel?: boolean;
	titleMethod?: any;
	defaults?: any;
	supplementWithDefaults?: boolean;
	entityTranslation?: string;
	entityType?: string;
	versioned?: boolean;
	useAutoSave?: boolean;
	showVersionMetadata?: boolean;
	standAlone?: boolean;
	versionMetadataToSave?: any;
	defaultVersionState?: string;
	extraEntityParams?: any;
	reduxConfig?: any;
	tabItems?: TabItem[];
	noReloadAfterSave?: boolean;
	versionIsPublished?: boolean;
	allowInvalidDrafts?: boolean;
	entityLoading?: boolean;
}

export interface EntityReduxConfig {
	store: any;
	undoableType: any;
}

@Directive()
export abstract class AbstractDetailsComponent implements OnInit, OnDestroy {

	activeVersion: SapiEntityVersion;
	activeId: string;
	activeTab = '';
	entity: SapiEntity;
	entityConfiguration: EntityConfiguration;
	entityVersionList: SapiEntityVersion[];
	form: NgForm;
	formGroup: UntypedFormGroup;
	isNew: boolean;
	routeOutlet: string;
	checklistItems: ChecklistItem[];

	protected entityVersionStates: any;
	protected fromEntity: SapiEntity;
	protected allowNavigation: boolean;
	protected entityTranslation: string;
	protected formChangesSub: Subscription;
	protected entityLoading = false;
	protected entityIsDuplicate = false;
	protected currentVersionState: string;
	protected useRedux = false;
	protected reduxInitialized = false;
	protected subscriptions: Subscription = new Subscription();
	protected tabsLoaded: string[];
	protected lastDetailComponentPath: string;

	// workaround so extending classes don't have to supply these services to us -- https://stackoverflow.com/a/40592524/4746171
	protected overlayService: OverlayService;
	protected sapiService: SapiService;
	protected translate: TranslateService;
	protected router: Router;
	protected hotkeysService: HotkeysService;
	protected authenticationService: AuthenticationService;
	protected reduxService: ReduxService;
	protected quickeditService: QuickeditService;
	protected modalRouteService: ModalRouteService;
	protected entityDetails: AbstractDetailsComponent;
	protected entityStoreService: EntityStoreService;

	protected skipFormGroupSave: string[];

	protected formHasChanges: boolean;
	protected isDestroyed = false;

	protected entityLoaded: Subject<void>;

	constructor(
		protected route: ActivatedRoute,
		protected location: Location
	) {
		this.entityDetails = this;
		this.overlayService = ServiceLocator.injector.get(OverlayService);
		this.sapiService = ServiceLocator.injector.get(SapiService);
		this.translate = ServiceLocator.injector.get(TranslateService);
		this.router = ServiceLocator.injector.get(Router);
		this.hotkeysService = ServiceLocator.injector.get(HotkeysService);
		this.authenticationService = ServiceLocator.injector.get(AuthenticationService);
		this.reduxService = ServiceLocator.injector.get(ReduxService);
		this.quickeditService = ServiceLocator.injector.get(QuickeditService);
		this.modalRouteService = ServiceLocator.injector.get(ModalRouteService);
		this.entityStoreService = ServiceLocator.injector.get(EntityStoreService);

		this.initNavigateAwayWarnings();
		this.initHotkeys();
		this.skipFormGroupSave = [];
		this.tabsLoaded = [];
		this.entityLoaded = new Subject<void>();
	}

	ngOnInit() {
		this.isDestroyed = false;
		if (this.route && this.route.outlet) {
			this.routeOutlet = this.route.outlet;
		}
		const _this = this;
		// used in the bb-actions-bar
		Object.defineProperty(this.entityConfiguration, 'entityLoading', {
			get() {
				return _this.entityLoading;
			}
		});

		this.currentVersionState = this.entityConfiguration.defaultVersionState || 'main';

		this.setFormGroup();

		const defaultTranslation = this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase();
		this.entityTranslation = (this.entityConfiguration.entityTranslation || defaultTranslation);

		if (this.entityConfiguration.reduxConfig) {
			this.initRedux();
		}

		if (this.form && this.checklistItems) {
			let changedIndex: number;
			this.subscriptions.add(this.form.form.valueChanges
				.pipe(
					debounceTime(300),
					pairwise(),
					map(([oldState, newState]) => {
						const changes = {};
						for (const key in newState) {
							if (oldState[key] !== newState[key]) {
								this.formHasChanges = true;
								changedIndex = this.checklistItems.findIndex(item => item && item.key === key);
								if (changedIndex !== -1) {
									changes[key] = newState[key];
								}
							}
						}
						return changes;
					}),
					filter(changes => Object.keys(changes).length !== 0 && !this.form.invalid),
				)
				.subscribe(p => {
					const val = Object.keys(p)[0];
					this.setChecklistItems(val, p[val], changedIndex);
				})
			);
		}

		if (this.entityConfiguration.standAlone) {
			if (this.entityConfiguration.defaults) {
				this.entity = bbDeepCopy(this.entityConfiguration.defaults);
			}

			this.isNew = !this.entity || !this.entity.id;

			if (this.isNew) {
				this.entityLoaded.next();
			} else {
				this.activeId = this.entity.id;
			}

			return;
		}

		this.entityConfiguration.defaults = this.entityConfiguration.defaults || {};

		this.subscriptions.add(this.route.params.subscribe((params: Params) => {
			this.activeTab = params.tab;
			this.activeId = params.id;
			const lastPath = this.router.url.replace(/\/[\w]+$/, '');
			if (lastPath !== this.lastDetailComponentPath) {
				this.tabsLoaded = [];
				this.lastDetailComponentPath = lastPath;
			}
			if (this.tabsLoaded.indexOf(this.activeTab) === -1) {
				this.tabsLoaded.push(this.activeTab);
			}

			if (this.activeId && this.activeId.startsWith('new')) {
				if (!this.isNew) {
					this.isNew = true;
					if (this.entityConfiguration.defaults) {
						this.entity = bbDeepCopy(this.entityConfiguration.defaults);
					}
					this.subscriptions.add(this.route.queryParams.subscribe((queryParams: any) => {
						if (queryParams && queryParams.fromId) {
							this.load(queryParams.fromId, true);
						}
						if (queryParams && queryParams.parentEntityType) {
							this.entity.parentEntityType = queryParams.parentEntityType;
						}
						if (queryParams && queryParams.parentEntityId) {
							this.entity.parentEntityId = queryParams.parentEntityId;
						}
					}));

					if (this.useRedux) {
						this.initializeReduxAsNew();
					}

					this.formGroup.markAsPristine();
				}
				this.entityLoaded.next();
			} else {
				if (this.isNew || !this.entity || this.entity.id !== params.id) {
					this.isNew = false;
					this.load(params.id);
				}
				this.isNew = false;
			}
			this.setFormGroup();

			if (this.entityConfiguration && this.entityConfiguration.useAutoSave && this.formGroup && !this.formChangesSub && !this.isDestroyed) {
				this.formChangesSub = this.formGroup.valueChanges.pipe(debounceTime(5000), distinctUntilChanged())
					.subscribe((value: any) => {
						if (!this.isNew && this.formGroup.dirty) {
							this.autoSave();
						}
					});
				this.subscriptions.add(this.reduxService.historyObs.pipe(debounceTime(5000), distinctUntilChanged())
					.subscribe((state: any) => {
						if (!this.isNew && this.formGroup.dirty) {
							this.autoSave();
						}
					}));
			}
		}));

		this.subscriptions.add(
			this.quickeditService.unsavedActionsUpdate().subscribe((unsavedActions: boolean) => {
				if (unsavedActions) {
					this.formGroup.markAsDirty();
				} else if (!this.formHasChanges) {
					this.formGroup.markAsPristine();
				}
			})
		);
	}

	ngOnDestroy() {
		this.isDestroyed = true;
		if (this.useRedux) {
			this.reduxService.dispatch({ history: 'reset', type: 'UNLOAD' });
		}
		this.subscriptions.unsubscribe();
		if (this.formChangesSub) {
			this.formChangesSub.unsubscribe();
			this.formChangesSub = null;
		}
	}

	initRedux() {
		this.useRedux = true;
		this.subscriptions.add(this.reduxService.historyObs.subscribe((state: any) => {
			this.reduxStateChanged(state);
		}));
	}

	tabLoadedBefore(tab: string) {
		return this.tabsLoaded.indexOf(tab) > -1;
	}

	reduxStateChanged(state: any) {
		if (state && state.present && state.present.type) {
			switch (state.present.type) {
				default:
					this.entity = state.present.data;
					if (this.formGroup) {
						this.formGroup.markAsDirty();
					}
					break;

				case 'LOADED':
					if (this.formGroup) {
						this.formGroup.markAsPristine();
					}
					break;

				case 'CREATE_NEW':
					if (this.formGroup) {
						this.formGroup.markAsPristine();
					}
					this.entity = state.present.data;
					break;
			}
		}
	}

	undo() {
		if (this.useRedux) {
			this.reduxService.dispatch({
				type: 'UNDO'
			});
		}
	}

	redo() {
		if (this.useRedux) {
			this.reduxService.dispatch({
				type: 'REDO'
			});
		}
	}

	initializeReduxAsNew(): void {
		if (!this.reduxInitialized) {
			this.reduxInitialized = true;
			this.reduxService.setup(this.entityConfiguration.reduxConfig.store, this.entityConfiguration.reduxConfig.undoableType);
		}
		this.reduxService.dispatch({
			type: this.entityConfiguration.reduxConfig.newAction, history: 'reset', mergeData: {
				parentEntityType: this.entity.parentEntityType,
				parentEntityId: this.entity.parentEntityId
			}
		});
	}

	setReduxStateFromEntity(entityData: SapiEntity, type?: string) {
		if (this.useRedux) {
			if (!this.reduxInitialized) {
				this.reduxInitialized = true;
				this.reduxService.setup(this.entityConfiguration.reduxConfig.store, this.entityConfiguration.reduxConfig.undoableType);
			}
			if (!entityData) {
				this.reduxService.dispatch({ history: 'reset', type: 'UNLOAD' });
			} else {
				if (this.entityConfiguration.reduxConfig.loadedAction) {
					this.reduxService.dispatch({ type: this.entityConfiguration.reduxConfig.loadedAction, history: 'reset', loadData: { data: entityData } });
				} else {
					this.reduxService.dispatch({ type: (type || 'FROM_LOAD'), history: 'reset', loadData: { data: entityData } });
				}
			}
		}
	}

	initHotkeys() {

		this.hotkeysService.add(new Hotkey('mod+s', (event: KeyboardEvent): boolean => {
			this.subscriptions.add(
				this.authenticationService.hasPermission('put').subscribe(hasRight => {
					if (hasRight) {
						this.save();
					}
				})
			);

			return false;
		}));

		// this.hotkeysService.add(new Hotkey('del', (event: KeyboardEvent): boolean => {
		// 	this.subscriptions.add(
		// 		this.authenticationService.hasPermission('delete').subscribe(hasRight => {
		// 			if (hasRight) {
		// 				this.delete();
		// 			}
		// 		})
		// 	);

		// 	return false;
		// }));
	}

	allowNavigateAway(currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> | boolean {
		// allow if not dirty
		if ((!this.formGroup || !this.formGroup.dirty) && !this.quickeditService.unsavedActions()) {
			this.allowNavigation = true;
			return true;
		}

		// allow if navigating to sibling route
		const currentParent = currentState.url.split('/').slice(0, -1).join('/');
		const nextParent = nextState.url.split('/').slice(0, -1).join('/');

		if (currentParent === nextParent) {
			this.allowNavigation = true;
			return true;
		}

		// ask user
		const allowNavigationObservable = new Subject<boolean>();

		const dialog = {
			icon: 'warning',
			title: this.translate.instant('PROMPT_SURE_TO_LEAVE_APPLICATION'),
			contents: this.translate.instant('PROMPT_SURE_TO_LEAVE_PAGE'),
			showSave: true,
			onConfirm: () => {
				this.quickeditService.removeSaveActions();
				this.allowNavigation = true;
				allowNavigationObservable.next(true);
			},
			onCancel: () => {
				allowNavigationObservable.next(false);
			},
			onSaveAndClose: () => {
				this.save();
				this.allowNavigation = true;
				allowNavigationObservable.next(true);
			}
		};

		this.overlayService.createDialog(dialog);

		return allowNavigationObservable;
	}

	initNavigateAwayWarnings() {

		if (this.allowNavigation) {
			return;
		}

		window.addEventListener('beforeunload', event => {

			if (this.formGroup && this.formGroup.dirty) {

				// most (all?) browsers will ignore this custom message, and will show a generic message
				const confirmationMessage = this.translate.instant('PROMPT_SURE_TO_LEAVE_APPLICATION');

				event.returnValue = confirmationMessage;	// Gecko, Trident, Chrome 34+
				return confirmationMessage;					// Gecko, WebKit, Chrome <34
			}
		});
	}

	setVersionState(newState: string) {
		if (newState) {
			this.currentVersionState = newState;
			if (this.entity && this.entity.id) {
				this.load(this.entity.id);
			}
		}
	}

	load(id: string, duplicating: boolean = false, version?: SapiEntityVersion): Observable<SapiEntity> {
		this.entityLoading = true;

		const extraOptions: any = {
			params: version ? {
				version: version.id
			} : {}
		};
		if (version && version.versionState) {
			extraOptions.state = version.versionState;
		} else if (this.currentVersionState && this.entityConfiguration.versioned) {
			extraOptions.state = this.currentVersionState;
		}
		if (this.entityConfiguration.extraEntityParams) {
			extraOptions.params = { ...extraOptions.params, ...this.entityConfiguration.extraEntityParams };
		}
		const getEntityObs = this.sapiService.get(this.entityConfiguration.name, id, '', extraOptions).pipe(
			catchError((err: SapiError) => {
				const notice: NoticeDefinition = {
					icon: 'warning',
					title: this.translate.instant('ERROR'),
					type: 'error',
					contents: this.translate.instant('ERROR_REQUESTING_ENTITY', {
						entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase(),
						errorMessage: (err.messages ? err.messages[0].message : ''),
						errorCode: err.code
					})
				};

				this.overlayService.createNotice(notice);
				return throwError(err);
			}));

		getEntityObs.subscribe((entity: SapiEntity) => {
			this.entityLoading = false;
			if (duplicating) {
				this.entityIsDuplicate = true;
				delete entity.id;
				const copy = this.translate.instant('COPY').toLowerCase();

				const entityMapping = this.entityStoreService.getEntityMapping(this.entityConfiguration.name);
				const labelProp = entityMapping.label || 'label';
				if (entity[labelProp]) {
					entity[labelProp] = `${entity[labelProp]} ${copy}`;
				}
				const valueProp = entityMapping.value || 'id';
				if (entity[valueProp] && entity[valueProp] !== 'id') {
					entity[valueProp] = `${entity[valueProp]}_${copy}`;
				}
			}
			if (entity.type !== 'Response') {
				if (this.entityConfiguration.supplementWithDefaults !== false && this.entityConfiguration.defaults) {
					this.entity = { ...bbDeepCopy(this.entityConfiguration.defaults), ...entity };
				} else {
					this.entity = { ...entity };
				}
				this.setReduxStateFromEntity(this.entity);
			}

			if (this.entityConfiguration.versioned && this.entity && this.entity.id) {
				this.activeVersion = version;
				this.getVersions(this.entity.id, this.currentVersionState, this.entityConfiguration.showVersionMetadata);
			}
			this.entityLoaded.next();
		});

		return getEntityObs;
	}

	loadVersion(version: SapiEntityVersion) {
		this.load(this.entity.id, false, version);
	}

	getLabel(): string {
		return this.entity.id;
	}

	handleViolations(err: SapiError): Subject<void> {

		const handleViolationsSubject = new Subject<void>();

		if (err.violations.length === 1 && err.violations[0].code === 'VERSION_CONSTRAINT') {

			const dialog: DialogDefinition = {
				icon: 'warning',
				title: this.translate.instant('CONFLICT_DETECTED_SAVING_SURE', {
					entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase()
				}),
				contents: this.translate.instant('OVERWRITE_ENTITY_ARE_YOU_SURE_WITH' + (err.violations[0].user ? '' : 'OUT') + '_USER', {
					entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase(),
					user: (err.violations[0].user || this.translate.instant('ANOTHER_USER').toLowerCase())
				}),
				onConfirm: () => {
					handleViolationsSubject.next();
				}
			};

			this.overlayService.createDialog(dialog);
		} else {

			const subMessages: string[] = [];

			err.violations.forEach(violation => {

				if (violation.code === 'FIELD_INVALID') {
					if (this.formGroup.controls[violation.field]) {
						const errors: ValidationErrors = {};
						errors['invalid-format'] = true;
						this.formGroup.controls[violation.field].setErrors(errors);
					}
					subMessages.push(this.translate.instant('VIOLATION_INVALID_FORMAT', { field: violation.field }));
				} else if (violation.code === 'UNIQUE_CONSTRAINT') {

					if (this.formGroup.controls[violation.field]) {
						const errors: ValidationErrors = {};
						errors['not-unique'] = true;
						this.formGroup.controls[violation.field].setErrors(errors);
					}

					subMessages.push(this.translate.instant('VIOLATION_UNIQUE_CONSTRAINT', { field: violation.field, entity: this.entityTranslation }));
				} else if (violation.code === 'VERSION_CONSTRAINT') {
					subMessages.push(this.translate.instant('VIOLATION_VERSION_CONSTRAINT', {
						entity: this.entityTranslation,
						user: (violation.user || this.translate.instant('ANOTHER_USER').toLowerCase())
					}));
				} else {
					subMessages.push(violation.text);
				}
			});

			const listitems = '<ul>' + subMessages.reduce((list: string, message: string) => list + '<li>' + message + '</li>', '') + '</ul>';

			const notice: NoticeDefinition = {
				icon: 'warning',
				type: 'error',
				title: this.translate.instant('VIOLATIONS_UPDATING_ENTITY_TITLE', {
					entity: this.entityTranslation
				}),
				contents: this.translate.instant('VIOLATIONS_UPDATING_ENTITY_' + (err.violations.length === 1 ? 'SINGLE' : 'MULTIPLE'), {
					entity: this.entityTranslation,
				}) + listitems
			};

			this.overlayService.createNotice(notice);
		}

		return handleViolationsSubject;
	}

	autoSave() {
		let entity: SapiEntity;

		if (this.form) {
			entity = this.entity;
		} else {
			entity = { ...this.entity, ...this.formGroup.value as SapiEntity };
		}

		this.sapiService.update(this.entityConfiguration.name, entity, true, { state: 'autosave' }).subscribe();
	}

	publish(force: boolean = false, extraHeaders?: any) {
		if (this.entity && this.entity.status === 'draft') {
			// to get a correct publisheddate
			this.entity.status = 'published';
		}
		this.save(force, extraHeaders, true, true);
	}

	save(force: boolean = false, extraHeaders?: any, saveDirectly: boolean = true, publish?: boolean): Observable<any> {
		const ignoreInvalid = !publish && this.entityConfiguration.allowInvalidDrafts;
		const saveSubject = new Subject<any>();
		let entity: SapiEntity;
		if (!extraHeaders) {
			extraHeaders = {};
		}

		if (this.form) {
			this.form.onSubmit({} as Event);
			entity = this.entity;
		} else {
			const formGroupValues: SapiEntity = { ...this.formGroup.value as SapiEntity };
			this.skipFormGroupSave.forEach((fieldName: string) => {
				if (formGroupValues.hasOwnProperty(fieldName)) {
					delete formGroupValues[fieldName];
				}
			});
			entity = { ...this.entity, ...formGroupValues };
		}

		if (this.formGroup && !this.formGroup.valid && !ignoreInvalid) {
			this.overlayService.createToast(this.translate.instant('COULD_NOT_SAVE_ENTITY_BECAUSE_REQUIRED_FIELDS2',
				{ entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase() }), 'times-circle');
			return saveSubject;
		}
		if (this.entityConfiguration.versionMetadataToSave) {
			extraHeaders = { ...extraHeaders, ...this.entityConfiguration.versionMetadataToSave };
			this.entityConfiguration.versionMetadataToSave = { bbcomment: '' };
		}
		if (!publish && this.currentVersionState && this.entityConfiguration.versioned) {
			extraHeaders.state = this.currentVersionState;
		}

		if (Object.keys(extraHeaders).length === 0) {
			extraHeaders = null;
		}

		const saveObservable = defer(() => {
			this.entityLoading = true;
			this.overlayService.setLoading(true);
			return this.sapiService[(this.isNew) ? 'create' : 'update'](this.entityConfiguration.name, entity, force, extraHeaders)
				.pipe(
					map((savedEntity: SapiEntity) => {
						this.formHasChanges = false;
						this.entityLoading = false;
						this.overlayService.setLoading(false);

						const previousId = this.entity.id ? this.entity.id : 'new';
						if (savedEntity.type !== 'Response') {
							// not an entity but a response message, don't update this.entity
							this.entity = savedEntity;
							this.setReduxStateFromEntity(this.entity);
						} else if (previousId.startsWith('new')) {
							if (savedEntity.hasOwnProperty('body')) {
								const regex = new RegExp(this.entityConfiguration.name + ' (\\d+)$', 'gi');

								let match = null;
								if ((match = regex.exec(savedEntity.body)) !== null) {
									match.forEach((id: string) => {
										this.entity.id = id;
									});
								}
							}
						}

						this.entityLoaded.next();
						saveSubject.next(entity);
						this.setFormGroup();

						if (!previousId || previousId === 'new') {
							this.formGroup.markAsPristine();
							if (!this.entityConfiguration.standAlone && !this.entityConfiguration.noReloadAfterSave) {
								if (this.routeOutlet === 'modal') {
									this.router.navigate(this.modalRouteService.convertToModalRoute([this.entityConfiguration.basePath, savedEntity.id]));
								} else {
									this.router.navigate([this.entityConfiguration.basePath, savedEntity.id]);
								}

							} else {
								this.entity = savedEntity;
							}
						} else {

							let value: any;
							if (this.form) {
								value = this.form.value;
							} else if (this.formGroup) {
								value = this.formGroup.value;
							}

							if (savedEntity.type !== 'Response' && this.formGroup && this.formGroup.patchValue) {
								this.formGroup.patchValue(value);
							}

							this.formGroup.markAsPristine();

							this.overlayService.createToast(this.translate.instant(publish ? 'ENTITY_PUBLISHED' : 'ENTITY_SAVED', {
								entity: this.entityTranslation,
								id: this.getLabel()
							}), publish ? 'check' : 'save');

							if (this.entityConfiguration.versioned && this.entity && this.entity.id) {
								this.activeVersion = null;
								this.getVersions(this.entity.id, this.currentVersionState, this.entityConfiguration.showVersionMetadata);
							}
						}
						if (ignoreInvalid) {
							for (const key in this.formGroup.controls) {
								if (this.formGroup.controls[key] && this.formGroup.controls[key].validator) {
									this.formGroup.controls[key].setErrors(null);
								}
							}
						}

						return this.entity;
					}),
					share(),
					catchError((err: SapiError) => {
						this.entityLoading = false;
						this.overlayService.setLoading(false);

						if (err && err.violations) {
							this.handleViolations(err).subscribe(() => {
								// try again, forced
								this.save(true, extraHeaders, saveDirectly, publish).subscribe((ent) => {
									if (!saveDirectly) {
										saveSubject.next(ent);
									}
								});
							});
						} else {
							let errorMessage = '';
							if (err && err.messages && err.messages.length && err.messages[0].userFriendlyLabel) {
								errorMessage = this.translate.instant(err.messages[0].userFriendlyLabel);
							} else {
								let errorCode = '0';
								if (err) {
									errorCode = err.code;
								}
								errorMessage = this.translate.instant('VIOLATIONS_UPDATING_ENTITY_TITLE', {
									entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase(),
									errorMessage,
									errorCode
								});
							}

							// fallback, show notice with backend error
							const notice: NoticeDefinition = {
								icon: 'warning',
								title: this.translate.instant('ERROR'),
								type: 'error',
								contents: errorMessage
							};

							this.overlayService.createNotice(notice);
						}
						return throwError(err);
					})
				);
		});

		if (saveDirectly) {
			saveObservable.subscribe();
		}
		return saveObservable.pipe(share());
	}

	delete(dialogContents?: string | TemplateRef<any>): Subject<void> {

		const deleteSubject = new Subject<void>();

		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETING_ENTITY', {
				entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase(),
				id: this.getLabel()
			}),
			contents: dialogContents ? dialogContents : this.translate.instant('DELETE_ENTITY_CONFIRM', {
				entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase(),
			}),
			onConfirm: () => {
				this.entityLoading = true;
				this.overlayService.setLoading(true);

				const deleteEntityObs = this.sapiService.delete(this.entityConfiguration.name, this.entity.id);
				this.setReduxStateFromEntity(null);

				deleteEntityObs.subscribe(() => {
					deleteSubject.next();
					this.overlayService.createToast(this.translate.instant('ENTITY_DELETED', {
						entity: this.entityTranslation,
						id: this.getLabel()
					}), 'trash');
					this.entityLoading = false;
					this.overlayService.setLoading(false);

					if (this.entityConfiguration.parentPath) {
						if (!this.entityConfiguration.standAlone) {
							setTimeout(() => this.router.navigate([this.entityConfiguration.parentPath]));
						}
					}
				}, (err: SapiError) => {
					// delete error
				});

				return deleteEntityObs;
			}
		};

		if (dialogContents && typeof dialogContents !== 'string') {
			dialog.templateRef = dialogContents;
		}
		this.overlayService.createDialog(dialog);

		return deleteSubject;
	}

	cancel(): Subject<SapiEntity> {
		this.formHasChanges = false;
		const cancelSubject = new Subject<SapiEntity>();
		if (this.isNew) {
			if (this.routeOutlet === 'modal') {
				this.modalRouteService.close();
			} else if (this.entityConfiguration.backOnNewCancel) {
				setTimeout(() => this.location.back());
			} else if (!this.entityConfiguration.standAlone && this.entityConfiguration.parentPath) {
				setTimeout(() => this.router.navigate([this.entityConfiguration.parentPath]));
			}
			cancelSubject.next(this.entity);
		} else {
			this.setFormGroup();
			this.load(this.entity.id).subscribe((entity: SapiEntity) => {
				this.setReduxStateFromEntity(this.entity);
				cancelSubject.next(entity);
				this.formGroup.markAsPristine();
			}, (err: SapiError) => {
				// cancel error
			});
		}

		return cancelSubject;
	}

	getVersions(entityId: string, state: string = 'main', includeMetadata: boolean = false): Observable<SapiEntity> {
		// version options:
		// ?metadata
		// ?allStates
		// ?state=autosave
		const extra = `versions?${state === 'allStates' ? 'allStates' : 'state=' + state}${includeMetadata ? '&metadata' : ''}`;
		const getVersionsObs = this.sapiService.get(this.entityConfiguration.name, entityId, extra).pipe(catchError((err: SapiError) => {
			const notice: NoticeDefinition = {
				icon: 'warning',
				title: this.translate.instant('ERROR'),
				type: 'error',
				contents: this.translate.instant('ERROR_REQUESTING_ENTITY', {
					entity: this.translate.instant(this.entityConfiguration.name.toUpperCase()).toLowerCase(),
					errorMessage: (err.messages ? err.messages[0].message : ''),
					errorCode: err.code
				})
			};

			this.overlayService.createNotice(notice);
			return throwError(err);
		}));

		getVersionsObs.subscribe((entityVersionResponse: any) => {
			this.entityVersionStates = { ...entityVersionResponse };
			this.entityVersionList =
				entityVersionResponse[this.currentVersionState] && entityVersionResponse[this.currentVersionState].items
					? [...entityVersionResponse[this.currentVersionState].items]
					: [];
			this.entityConfiguration.versionIsPublished = (this.entityVersionList || []).length && this.entityVersionList[0].metadata.alsopublished === 'true';
		});

		return getVersionsObs;
	}

	setChecklistItems(key: string, val: any, index: number) {
		if (!index) { // for thumbnails or other items that are not in the form
			index = this.checklistItems.findIndex(item => item && item.key === key);
		}
		let valid: boolean;
		switch (key) {
			case 'status':
				valid = val === 'published';
				break;
			case 'thumbnails':
				valid = (val && val.some((thumbnail: any) => thumbnail.main === true));
				break;
			default:
				valid = !!val;
				break;
		}
		if (this.checklistItems && this.checklistItems[index]) {
			this.checklistItems[index].checked = valid;
		}
	}

	setFormGroup() {
		if (this.form && this.form instanceof NgForm && this.form.form) {
			this.formGroup = this.form.form;
		}
	}
}
